// Pure progress + visibility helpers shared between the API Worker and the
// web app. Operates on minimal structural types so each caller can pass its
// own (DB row vs frontend) shapes without conversion.

export type ProgressAdoptionTrigger = {
  trigger_value: string;
  document_id: string;
};

// Recursive AND/OR visibility expression. The zod schema in schemas.ts is
// typed against this — progress.ts stays dependency-free.
export type VisibilityRuleValue = string | number | boolean | string[];
export type VisibilityRule =
  | { q: string; value: VisibilityRuleValue }
  | { op: 'and' | 'or'; children: VisibilityRule[] };

export type ProgressQuestion = {
  id: string;
  type: string;
  required: boolean;
  requires_upload: boolean;
  parent_id: string | null;
  condition: { value: unknown } | null;
  visibility_rule: VisibilityRule | null;
  adoptable_doc_id: string | null;
  adoption_triggers: ProgressAdoptionTrigger[];
  video_url?: string | null;
};

export type ProgressSubmission = {
  question_id: string;
  answer: unknown;
  submission_files: { id: string; option_key?: string | null }[];
  submission_adoptions: { document_id: string; option_key?: string | null }[];
};

export type CompletionReason =
  | 'no_answer'
  | 'upload_required'
  | 'adoption_required'
  | 'video_unwatched';

export type ProgressResult = {
  progress: number;
  totalRequired: number;
  completedRequired: number;
  visibleIds: Set<string>;
  missing: Array<{ question_id: string; reason: CompletionReason }>;
};

export function matchesCondition(answer: unknown, expected: unknown): boolean {
  if (Array.isArray(expected)) {
    if (!Array.isArray(answer)) return false;
    return expected.every((v) => (answer as unknown[]).includes(v));
  }
  if (Array.isArray(answer)) return (answer as unknown[]).includes(expected);
  return answer === expected;
}

export function isVisible(
  q: ProgressQuestion,
  byId: Map<string, ProgressQuestion>,
  subByQ: Map<string, ProgressSubmission>,
  visiting: Set<string> = new Set(),
): boolean {
  // Cycle backstop: the API rejects circular dependencies at write time; if
  // legacy bad data contains one anyway, hide the question rather than
  // recurse forever. Hidden is the safe failure mode (can't block progress).
  if (visiting.has(q.id)) return false;
  visiting.add(q.id);
  try {
    if (q.visibility_rule) return evalRule(q.visibility_rule, byId, subByQ, visiting);
    if (!q.parent_id) return true;
    const parent = byId.get(q.parent_id);
    if (!parent) return false;
    if (!isVisible(parent, byId, subByQ, visiting)) return false;
    const parentSub = subByQ.get(parent.id);
    if (!parentSub) return false;
    if (!q.condition) return true;
    return matchesCondition(parentSub.answer, q.condition.value);
  } finally {
    visiting.delete(q.id);
  }
}

// A leaf is true iff the referenced question is itself visible, answered,
// and the answer matches (same equals/contains semantics as the legacy
// single-parent path). Unknown refs are false — deletion pruning in the API
// should make that unreachable.
function evalRule(
  rule: VisibilityRule,
  byId: Map<string, ProgressQuestion>,
  subByQ: Map<string, ProgressSubmission>,
  visiting: Set<string>,
): boolean {
  if ('op' in rule) {
    // Empty groups are schema-forbidden; if bad data slips through, hide
    // (consistent with the cycle backstop's safe failure mode).
    if (rule.children.length === 0) return false;
    return rule.op === 'and'
      ? rule.children.every((c) => evalRule(c, byId, subByQ, visiting))
      : rule.children.some((c) => evalRule(c, byId, subByQ, visiting));
  }
  const ref = byId.get(rule.q);
  if (!ref) return false;
  if (!isVisible(ref, byId, subByQ, visiting)) return false;
  const sub = subByQ.get(ref.id);
  if (!sub) return false;
  return matchesCondition(sub.answer, rule.value);
}

export function visibleQuestionSet(
  questions: ProgressQuestion[],
  subByQ: Map<string, ProgressSubmission>,
): Set<string> {
  const byId = new Map<string, ProgressQuestion>();
  for (const q of questions) byId.set(q.id, q);
  const visible = new Set<string>();
  for (const q of questions) {
    if (isVisible(q, byId, subByQ)) visible.add(q.id);
  }
  return visible;
}

export function isQuestionComplete(
  q: ProgressQuestion,
  sub: ProgressSubmission | undefined,
): CompletionReason | null {
  if (q.type === 'informational') {
    // Informational questions without a video are always complete — they
    // are read-only content with no interaction needed. With a video,
    // the company must record that they watched it.
    if (!q.video_url) return null;
    if (!sub) return 'video_unwatched';
    const a = sub.answer as { watched_at?: unknown } | null | undefined;
    if (a && typeof a === 'object' && !Array.isArray(a) && typeof a.watched_at === 'string') {
      return null;
    }
    return 'video_unwatched';
  }

  if (q.type === 'image_info') {
    // Image-info questions are read-only display content (image + text)
    // and never require an answer.
    return null;
  }

  if (!sub) return 'no_answer';

  if (q.type === 'document_adopt') {
    if (!q.adoptable_doc_id) return null;
    const has = sub.submission_adoptions.some((a) => a.document_id === q.adoptable_doc_id);
    return has ? null : 'adoption_required';
  }

  if (q.type === 'document_upload') {
    // A document_upload question is satisfied by either an uploaded file or an
    // adoption row for the linked standard document (when one is configured).
    if (q.adoptable_doc_id) {
      const has = sub.submission_adoptions.some((a) => a.document_id === q.adoptable_doc_id);
      if (has) return null;
    }
    return sub.submission_files.length > 0 ? null : 'upload_required';
  }

  if (q.type === 'signature') {
    if (!sub.answer || typeof sub.answer !== 'object' || Array.isArray(sub.answer)) {
      return 'no_answer';
    }
    const sig = sub.answer as {
      signature_data_url?: unknown;
      signer_name?: unknown;
      signed_at?: unknown;
    };
    if (
      typeof sig.signature_data_url !== 'string' ||
      sig.signature_data_url.length === 0 ||
      typeof sig.signer_name !== 'string' ||
      sig.signer_name.trim() === '' ||
      typeof sig.signed_at !== 'string' ||
      sig.signed_at.length === 0
    ) {
      return 'no_answer';
    }
    return null;
  }

  if (q.type === 'multiselect_uploads') {
    if (sub.answer === null || sub.answer === undefined) return 'no_answer';
    if (!Array.isArray(sub.answer) || sub.answer.length === 0) return 'no_answer';
    const triggers = new Map(q.adoption_triggers.map((t) => [t.trigger_value, t.document_id]));
    for (const opt of sub.answer as unknown[]) {
      const key = String(opt);
      const adoptableDoc = triggers.get(key);
      const adopted =
        adoptableDoc !== undefined &&
        sub.submission_adoptions.some(
          (a) => a.document_id === adoptableDoc && (a.option_key ?? null) === key,
        );
      if (adopted) continue;
      const hasFile = sub.submission_files.some((f) => (f.option_key ?? null) === key);
      if (!hasFile) return 'upload_required';
    }
    return null;
  }

  if (sub.answer === null || sub.answer === undefined) return 'no_answer';
  if (typeof sub.answer === 'string' && sub.answer.trim() === '') return 'no_answer';
  if (Array.isArray(sub.answer) && sub.answer.length === 0) return 'no_answer';

  if (q.requires_upload && sub.submission_files.length === 0) return 'upload_required';

  for (const trigger of q.adoption_triggers) {
    if (matchesCondition(sub.answer, trigger.trigger_value)) {
      const has = sub.submission_adoptions.some((a) => a.document_id === trigger.document_id);
      if (!has) return 'adoption_required';
    }
  }
  return null;
}

// ----------------------------------------------------------------------
// Company information completeness
// ----------------------------------------------------------------------

// Fields the company must populate before they can submit for review.
// trustee_info is omitted because it is only required "if applicable".
export const REQUIRED_COMPANY_INFO_FIELDS = [
  'abn',
  'primary_contact_name',
  'contact_role',
  'contact_phone',
  'contact_email',
  'employee_count',
] as const;

export type RequiredCompanyInfoField = (typeof REQUIRED_COMPANY_INFO_FIELDS)[number];

export type CompanyInfoForGate = Partial<Record<RequiredCompanyInfoField, unknown>>;

export function missingCompanyInfoFields(
  info: CompanyInfoForGate,
): RequiredCompanyInfoField[] {
  const missing: RequiredCompanyInfoField[] = [];
  for (const field of REQUIRED_COMPANY_INFO_FIELDS) {
    const value = info[field];
    if (value === null || value === undefined) {
      missing.push(field);
      continue;
    }
    if (typeof value === 'string' && value.trim() === '') missing.push(field);
  }
  return missing;
}

export function computeProgress(
  questions: ProgressQuestion[],
  submissions: ProgressSubmission[],
): ProgressResult {
  const subByQ = new Map<string, ProgressSubmission>();
  for (const s of submissions) subByQ.set(s.question_id, s);

  const visibleIds = visibleQuestionSet(questions, subByQ);

  let totalRequired = 0;
  let completedRequired = 0;
  const missing: ProgressResult['missing'] = [];

  for (const q of questions) {
    if (!visibleIds.has(q.id)) continue;
    if (!q.required) continue;
    totalRequired += 1;
    const reason = isQuestionComplete(q, subByQ.get(q.id));
    if (reason === null) {
      completedRequired += 1;
    } else {
      missing.push({ question_id: q.id, reason });
    }
  }

  const progress =
    totalRequired === 0 ? 100 : Math.round((completedRequired * 100) / totalRequired);
  return { progress, totalRequired, completedRequired, visibleIds, missing };
}
